import clsx from 'clsx';
import {
  Children,
  cloneElement,
  isValidElement,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { CSSTransition } from 'react-transition-group';

import { useEventListener } from 'src/hooks/use-event-listener';
import { useOnClickOutside } from 'src/hooks/use-on-click-outside';
import { useToggle } from 'src/hooks/use-toggle';

import { DROPDOWN_ANIMATION_DURATION } from './constants';
import { DropdownItem, DropdownItemProps } from './dropdown-item';

import './dropdown.scss';

export type DropdownProps = {
  children: ReactNode;
  onSelect?: (id: string, text: string, eventType?: string) => void;
  dropdownText?: string;
  customDropdownText?: string;
  loadingText?: string;
  isLoading?: boolean;
  // receive initialSelectedId as array to use the same value to reset selected option from parent component
  initialSelectedId?: string[];
  classNames?: {
    container?: string;
    button?: string;
    menu?: string;
  };
  isDisabled?: boolean;
  testId?: string;
  ariaLabel?: string;
};

export const Dropdown = ({
  children,
  onSelect,
  dropdownText = 'Select an item',
  customDropdownText,
  loadingText = 'Loading...',
  isLoading = false,
  classNames,
  initialSelectedId,
  isDisabled,
  testId = 'dropdown',
  ariaLabel,
}: DropdownProps) => {
  const [isExpanded, toggleExpanded] = useToggle(false);
  const [isUsingKeyboard, setUsingKeyboard] = useState(false);
  const [activeItemIndex, setActiveItemIndex] = useState(-1);
  const [selectedItemId, setSelectedItemId] = useState<string>();
  const [buttonText, setButtonText] = useState(dropdownText);
  const [shouldShow, setShouldShow] = useState(false);
  const firstLoad = useRef(true);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const menuEl = useRef<HTMLUListElement | null>(null);
  const buttonEl = useRef<HTMLButtonElement | null>(null);

  const isMoreThanOneItem = Children.toArray(children).length > 1;

  const runningTimerId = useRef<NodeJS.Timeout | undefined>(undefined);

  // clear pending timer if component has been unmounted
  useEffect(() => {
    return () => {
      clearTimeout(runningTimerId.current);
    };
  }, []);

  // need this hook to set new init value after initialSelectedId prop changed.
  useEffect(() => {
    firstLoad.current = true;
  }, [initialSelectedId]);

  useEffect(() => {
    if (!customDropdownText) return;

    setButtonText(customDropdownText);
  }, [customDropdownText]);

  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;

      if (initialSelectedId) {
        setSelectedItemId(initialSelectedId[0]);
        Children.forEach(children, (item) => {
          if (!isValidElement<DropdownItemProps>(item)) return;
          if (item.props.id === initialSelectedId[0]) {
            setButtonText(item.props.children);
          }
        });
      }
      return;
    }

    if (isExpanded) {
      menuEl.current?.focus();
    }
  }, [children, initialSelectedId, isExpanded, isUsingKeyboard]);

  useOnClickOutside(dropdownRef, () => {
    if (isExpanded) {
      setShouldShow(false);

      runningTimerId.current = setTimeout(() => {
        closeDropdown();
      }, DROPDOWN_ANIMATION_DURATION);
    }
  });

  useEventListener('mouseup', () => setUsingKeyboard(false));

  const closeDropdown = () => {
    toggleExpanded();
    setActiveItemIndex(-1);
  };

  const handleSelectItem = (id: string, text: string, eventType?: string) => {
    setShouldShow(false);

    runningTimerId.current = setTimeout(() => {
      if (eventType !== 'contextmenu') {
        closeDropdown();
        setButtonText(text);
        setSelectedItemId(id);
      }
      onSelect?.(id, text, eventType);

      if (isUsingKeyboard) {
        buttonEl.current?.focus();
      }
    }, DROPDOWN_ANIMATION_DURATION);
  };

  const handleClickItem = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const elId = target.getAttribute('id') ?? '';
    const elText = target.textContent ?? '';

    handleSelectItem(elId, elText, e.type);
  };

  const handleDropdownButtonClick = (e: MouseEvent) => {
    // Prevent multiple instantaneous clicks.
    if (e.detail > 1) return;

    if (!isMoreThanOneItem) return;

    if (isExpanded) {
      setShouldShow(false);
      runningTimerId.current = setTimeout(() => {
        toggleExpanded();
      }, DROPDOWN_ANIMATION_DURATION);
    } else {
      setShouldShow(true);
      toggleExpanded();
    }
  };

  const handleKeydownDropdownButtonClick = (
    e: KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (e.key === 'Tab') return;
    setUsingKeyboard(true);
  };

  const focusNextActiveItem = () => {
    const length = Children.count(children);
    if (!length) return;
    setActiveItemIndex((prev) => (activeItemIndex < length - 1 ? prev + 1 : 0));
  };

  const focusPreviousActiveItem = () => {
    const length = Children.count(children);
    if (!length) return;
    setActiveItemIndex((prev) => (activeItemIndex > 0 ? prev - 1 : length - 1));
  };

  const handleEnter = () => {
    if (activeItemIndex < 0) return;

    const childrenArray = Children.toArray(children);
    const el = childrenArray[activeItemIndex];

    if (!isValidElement<DropdownItemProps>(el)) return;

    const elId = el.props.id;
    const elText = el.props.children;
    handleSelectItem(elId, elText);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Tab') setUsingKeyboard(true);

    if (!isExpanded) return;
    e.preventDefault();

    if (e.key === 'Tab' || e.key === 'Escape') {
      setShouldShow(false);

      runningTimerId.current = setTimeout(() => {
        closeDropdown();
      }, DROPDOWN_ANIMATION_DURATION);
    } else if (e.key === ' ' || e.key === 'Enter') {
      handleEnter();
    } else if (e.key === 'ArrowDown') {
      focusNextActiveItem();
    } else if (e.key === 'ArrowUp') {
      focusPreviousActiveItem();
    }
  };

  const dropdownItems = Children.map(children, (child, index) => {
    if (!isValidElement<DropdownItemProps>(child)) return;
    return cloneElement(child, {
      onClick: handleClickItem,
      onContextMenu: handleClickItem,
      isFocused: index === activeItemIndex,
      isSelected: child.props.id === selectedItemId,
      className: clsx({
        'faux-focus': index === activeItemIndex && isUsingKeyboard,
        selected: child.props.id === selectedItemId,
      }),
    });
  });

  return (
    <div
      ref={dropdownRef}
      className={clsx('dropdown', classNames?.container, {
        open: isExpanded,
      })}
      onKeyDown={handleKeyDown}
      data-testid={testId}
    >
      <button
        className={clsx('dropdown__btn', classNames?.button, {
          'dropdown__btn--disabled': isDisabled,
        })}
        ref={buttonEl}
        type="button"
        aria-haspopup={!isDisabled}
        aria-expanded={isExpanded}
        aria-disabled={isDisabled}
        aria-label={ariaLabel}
        disabled={isDisabled}
        onClick={handleDropdownButtonClick}
        onKeyDown={handleKeydownDropdownButtonClick}
        data-testid="dropdown-btn"
        tabIndex={isDisabled ? -1 : 0}
      >
        <span className="dropdown__btn-text">
          {isLoading ? (
            <>
              {/* <Spinner isCentered={false} isDark />*/}
              {loadingText}
            </>
          ) : (
            <>{buttonText}</>
          )}
        </span>
        {isMoreThanOneItem && (
          <i className="icon-v2-caret-small-down dropdown__btn-icon" />
        )}
      </button>
      <CSSTransition
        nodeRef={menuEl}
        in={shouldShow}
        timeout={DROPDOWN_ANIMATION_DURATION}
        classNames="rotateX"
        unmountOnExit
      >
        <ul
          className={clsx('dropdown__menu', classNames?.menu)}
          ref={menuEl}
          role="listbox"
          aria-label="Dropdown menu"
          data-testid="dropdown-menu"
        >
          {dropdownItems}
        </ul>
      </CSSTransition>
    </div>
  );
};

Dropdown.Item = DropdownItem;
